import { Link, NavLink } from "react-router-dom";
import { Ticket, Menu, X, ArrowRight } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { me } = useAuth();
  const link = ({ isActive }) =>
    `text-sm font-semibold tracking-wide transition-colors ${
      isActive ? "text-white" : "text-slate-300 hover:text-white"
    }`;

  // If logged in, scanner accounts should be sent straight to /admin/scan; everyone
  // else lands on /admin. This is what makes the "Terug naar beheer" pill work
  // AND signals that the session is still alive after clicking Home.
  const adminHref = me?.role === "scanner" ? "/admin/scan" : "/admin";

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-[#0f1a2e]/90 backdrop-blur-xl border-b border-svj-border"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-svj-orange via-svj-pink to-svj-purple">
            <Ticket className="h-5 w-5 text-white" />
          </span>
          <span className="text-white font-extrabold tracking-tight text-lg">
            StudioVoorJou<span className="text-svj-cyan"> Tickets</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={link} data-testid="nav-home">Home</NavLink>
          <NavLink to="/events" className={link} data-testid="nav-events">Events</NavLink>
          {!me && (
            <NavLink to="/admin/login" className={link} data-testid="nav-admin">Beheer</NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {me ? (
            <Link
              to={adminHref}
              data-testid="nav-back-to-admin"
              className="inline-flex items-center gap-1.5 rounded-full border border-svj-cyan/60 text-svj-cyan font-bold py-2 px-5 text-sm hover:bg-svj-cyan/10 transition-all"
              title={`Ingelogd als ${me.first_name || me.email}`}
            >
              Terug naar beheer <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <Link
              to="/events"
              data-testid="nav-cta"
              className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2 px-5 text-sm hover:shadow-[0_0_20px_rgba(214,61,122,0.5)] transition-all"
            >
              Bekijk events
            </Link>
          )}
        </div>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-svj-border bg-[#0f1a2e]" data-testid="nav-mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-4">
            <NavLink to="/" end className={link} onClick={() => setOpen(false)}>Home</NavLink>
            <NavLink to="/events" className={link} onClick={() => setOpen(false)}>Events</NavLink>
            {me ? (
              <Link
                to={adminHref}
                data-testid="nav-mobile-back-to-admin"
                onClick={() => setOpen(false)}
                className="text-svj-cyan font-semibold"
              >
                Terug naar beheer →
              </Link>
            ) : (
              <NavLink to="/admin/login" className={link} onClick={() => setOpen(false)}>Beheer</NavLink>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
